import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import POSForm from '../../Components/Worldmap/POSForm';
import POSProcessor from './POSProcessor';
import { worldmapStyles } from './worldmapStyles';
import EmbeddedMapViewer from '../../Components/Worldmap/EmbeddedMapViewer';

const POSManager = () => {
    const [loading, setLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [error, setError] = useState('');
    const [pointsOfSale, setPointsOfSale] = useState([]);
    const [editingPOS, setEditingPOS] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [totalPOS, setTotalPOS] = useState(0);
    const [isFormCollapsed, setIsFormCollapsed] = useState(true);
    const [showCSVUploader, setShowCSVUploader] = useState(false);
    const [activeTab, setActiveTab] = useState('list');
    const [selectedItemForMap, setSelectedItemForMap] = useState(null);

    useEffect(() => {
        fetchPointsOfSale();
    }, []);

    const fetchPointsOfSale = async () => {
        try {
            setLoading(true);
            
            const { count, error: countError } = await supabase
                .from('points_of_sale')
                .select('*', { count: 'exact', head: true });
            
            if (!countError) {
                setTotalPOS(count || 0);
            }

            const { data, error } = await supabase
                .from('points_of_sale')
                .select('*')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setPointsOfSale(data || []);
        } catch (error) {
            setError('Erreur lors du chargement des points de vente : ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (posId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce point de vente ?')) {
            try {
                setLoading(true);
                const { error } = await supabase
                    .from('points_of_sale')
                    .delete()
                    .eq('id', posId);

                if (error) throw error;
                
                setFeedback('Point de vente supprimé avec succès');
                fetchPointsOfSale();
            } catch (error) {
                setError('Erreur lors de la suppression du point de vente : ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (pos) => {
        setEditingPOS(pos);
        setIsEditing(true);
        setIsFormCollapsed(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const filteredPOS = pointsOfSale.filter(pos => {
        const searchLower = searchQuery.toLowerCase();
        return (
            pos.titre_affiche?.toLowerCase().includes(searchLower) ||
            pos.adresse?.toLowerCase().includes(searchLower) ||
            pos.design_points_of_sales?.toLowerCase().includes(searchLower)
        );
    });

    const styles = {
        tabContainer: {
            marginBottom: '30px',
            display: 'flex',
            gap: '10px'
        }
    };

    return (
        <div>
            {/* Embedded Map Viewer */}
            {selectedItemForMap && (
                <EmbeddedMapViewer 
                    item={selectedItemForMap}
                    type="pos"
                    onClose={() => setSelectedItemForMap(null)}
                />
            )}

            {/* Statistics Card */}
            <div style={worldmapStyles.statsCard}>
                <div>
                    <div style={worldmapStyles.statsNumber}>{totalPOS}</div>
                    <div style={worldmapStyles.statsLabel}>Total des Points de Vente</div>
                </div>
                <div style={{ fontSize: '60px', opacity: '0.3' }}>🏪</div>
            </div>

            {/* Tab Navigation */}
            <div style={styles.tabContainer}>
                <button 
                    style={{
                        ...worldmapStyles.tabButton,
                        ...(activeTab === 'list' ? worldmapStyles.activeTabButton : {})
                    }}
                    onClick={() => setActiveTab('list')}
                >
                    <span style={{ marginRight: '8px' }}>📋</span>
                    Gérer PDV
                </button>
                <button 
                    style={{
                        ...worldmapStyles.tabButton,
                        ...(activeTab === 'upload' ? worldmapStyles.activeTabButton : {})
                    }}
                    onClick={() => setActiveTab('upload')}
                >
                    <span style={{ marginRight: '8px' }}>📁</span>
                    Téléchargement CSV
                </button>
            </div>

            {/* Feedback Messages */}
            {loading && (
                <div style={{ ...worldmapStyles.alert, backgroundColor: '#cfe2ff', color: '#084298', border: '1px solid #b6d4fe' }}>
                    <span style={{ marginRight: '10px' }}>⏳</span> Traitement en cours...
                </div>
            )}
            {feedback && (
                <div style={{ ...worldmapStyles.alert, ...worldmapStyles.successAlert }}>
                    <span style={{ marginRight: '10px' }}>✅</span> {feedback}
                </div>
            )}
            {error && (
                <div style={{ ...worldmapStyles.alert, ...worldmapStyles.errorAlert }}>
                    <span style={{ marginRight: '10px' }}>⚠️</span> {error}
                </div>
            )}

            {/* List Tab Content */}
            {activeTab === 'list' && (
                <>
                    {/* Add/Edit Form */}
                    {!isEditing && (
                        <div style={worldmapStyles.formSection}>
                            <div 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    cursor: 'pointer',
                                    marginBottom: '20px',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    transition: 'background-color 0.3s ease'
                                }}
                                onClick={() => setIsFormCollapsed(!isFormCollapsed)}
                            >
                                <span style={{ 
                                    marginRight: '15px', 
                                    fontSize: '24px',
                                    transform: isFormCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                                    transition: 'transform 0.3s ease',
                                    display: 'inline-block'
                                }}>
                                    ▶
                                </span>
                                <h3 style={{ margin: 0, color: '#212529' }}>
                                    <span style={{ marginRight: '10px' }}>➕</span>
                                    Ajouter un Nouveau Point de Vente
                                </h3>
                            </div>
                            {!isFormCollapsed && (
                                <div style={{ animation: 'fadeIn 0.4s ease-out' }}>
                                    <POSForm 
                                        onSuccess={() => {
                                            fetchPointsOfSale();
                                            setFeedback('Point de vente ajouté avec succès ! 🎉');
                                            setIsFormCollapsed(true);
                                        }}
                                        onError={(error) => setError(error.message)}
                                        isEditing={false}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {isEditing && editingPOS && (
                        <div style={worldmapStyles.formSection}>
                            <div style={{ marginBottom: '20px' }}>
                                <button 
                                    onClick={() => {
                                        setIsEditing(false);
                                        setEditingPOS(null);
                                    }}
                                    style={{
                                        ...worldmapStyles.button,
                                        backgroundColor: '#6c757d',
                                        color: 'white'
                                    }}
                                    className="button-hover"
                                >
                                    ← Retour à la Liste
                                </button>
                            </div>
                            <h3 style={{ marginBottom: '20px', color: '#212529' }}>
                                <span style={{ marginRight: '10px' }}>✏️</span>
                                Modifier le Point de Vente
                            </h3>
                            <POSForm 
                                initialData={editingPOS}
                                isEditing={true}
                                onSuccess={() => {
                                    fetchPointsOfSale();
                                    setIsEditing(false);
                                    setEditingPOS(null);
                                    setFeedback('Point de vente mis à jour avec succès ! ✨');
                                }}
                                onError={(error) => setError(error.message)}
                            />
                        </div>
                    )}

                    {/* POS List */}
                    <div style={{ marginTop: '40px' }}>
                        <h3 style={{ marginBottom: '30px', fontSize: '24px', fontWeight: '600', color: '#212529' }}>
                            Points de Vente Existants
                        </h3>
                        
                        {/* Search Bar */}
                        <input
                            type="text"
                            placeholder="🔍 Rechercher par titre, adresse ou type..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={worldmapStyles.searchBar}
                            className="search-focus"
                        />

                        {/* POS Table */}
                        <div style={worldmapStyles.listContainer}>
                            {filteredPOS.length === 0 ? (
                                <div style={worldmapStyles.emptyState}>
                                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔍</div>
                                    <h4>Aucun point de vente trouvé</h4>
                                    <p>Essayez d'ajuster vos critères de recherche ou ajoutez un nouveau point de vente</p>
                                </div>
                            ) : (
                                filteredPOS.map(pos => (
                                    <div 
                                        key={pos.id} 
                                        style={{
                                            ...worldmapStyles.listItem,
                                            display: 'grid',
                                            gridTemplateColumns: '120px 1fr 100px',
                                            gap: '25px',
                                            alignItems: 'center',
                                            minHeight: '140px',
                                            padding: '25px'
                                        }}
                                        className="list-item-hover"
                                    >
                                        {/* Main Logo/Image */}
                                        <div style={{
                                            width: '120px',
                                            height: '120px',
                                            flexShrink: 0,
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            backgroundColor: '#f8f9fa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
                                            border: '3px solid #e9ecef'
                                        }}>
                                            {pos.photos_projet ? (
                                                <img
                                                    src={pos.photos_projet.split(',')[0].trim()}
                                                    alt="Logo Point de Vente"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'contain',
                                                        padding: '8px'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div style={{
                                                display: pos.photos_projet ? 'none' : 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '100%',
                                                height: '100%',
                                                fontSize: '36px',
                                                color: '#999'
                                            }}>
                                                🏪
                                            </div>
                                        </div>
                                        
                                        {/* POS Info */}
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '20px', fontWeight: '700', color: '#212529', marginBottom: '8px' }}>
                                                {pos.titre_affiche || 'Sans Titre'}
                                            </div>
                                            <p style={{ margin: '8px 0', color: '#666', fontSize: '14px', lineHeight: '1.5' }}>
                                                📍 {pos.adresse}
                                            </p>
                                            {pos.design_points_of_sales && (
                                                <span style={{
                                                    ...worldmapStyles.badge,
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    padding: '8px 16px',
                                                    fontSize: '13px',
                                                    fontWeight: '600'
                                                }}>
                                                    🏪 {pos.design_points_of_sales}
                                                </span>
                                            )}
                                            
                                            {/* Additional Images */}
                                            {pos.photos_projet && pos.photos_projet.split(',').length > 1 && (
                                                <div style={{ 
                                                    marginTop: '15px',
                                                    display: 'flex',
                                                    gap: '10px',
                                                    flexWrap: 'wrap'
                                                }}>
                                                    <span style={{ fontSize: '12px', color: '#6c757d', marginRight: '10px', alignSelf: 'center' }}>Images supplémentaires :</span>
                                                    {pos.photos_projet.split(',').slice(1, 4).map((url, index) => {
                                                        const trimmedUrl = url.trim();
                                                        if (!trimmedUrl) return null;
                                                        
                                                        return (
                                                            <div key={index} style={{
                                                                width: '60px',
                                                                height: '60px',
                                                                backgroundColor: '#f0f0f0',
                                                                borderRadius: '12px',
                                                                overflow: 'hidden',
                                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                                                            }}>
                                                                <img
                                                                    src={trimmedUrl}
                                                                    alt={`Image ${index + 2}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'contain'
                                                                    }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                    {pos.photos_projet.split(',').length > 4 && (
                                                        <div style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            backgroundColor: '#e0e0e0',
                                                            borderRadius: '12px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '12px',
                                                            color: '#666',
                                                            fontWeight: '600'
                                                        }}>
                                                            +{pos.photos_projet.split(',').length - 4}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Action Buttons */}
                                        <div style={{ 
                                            display: 'flex', 
                                            gap: '8px', 
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexDirection: 'column'
                                        }}>
                                            <button
                                                onClick={() => {
                                                    setSelectedItemForMap(pos);
                                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                                }}
                                                style={{ 
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: 'white',
                                                    width: '100px',
                                                    height: '32px',
                                                    padding: '0 12px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '20px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    gap: '4px',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    marginBottom: '8px'
                                                }}
                                                className="button-hover map-button"
                                                title="Voir sur la map"
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(102, 126, 234, 0.4)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                                                }}
                                            >
                                                🌍 Map
                                            </button>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button
                                                    onClick={() => handleEdit(pos)}
                                                    style={{ 
                                                        ...worldmapStyles.button, 
                                                        ...worldmapStyles.editButton,
                                                        width: '44px',
                                                        height: '44px',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '10px',
                                                        fontSize: '20px',
                                                        color: 'white',
                                                        fontWeight: 'normal'
                                                    }}
                                                    className="button-hover icon-button"
                                                    title="Modifier ce point de vente"
                                                >
                                                    ✎
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(pos.id)}
                                                    style={{ 
                                                        ...worldmapStyles.button, 
                                                        ...worldmapStyles.deleteButton,
                                                        width: '44px',
                                                        height: '44px',
                                                        padding: '0',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '10px',
                                                        fontSize: '20px',
                                                        color: 'white',
                                                        fontWeight: 'normal'
                                                    }}
                                                    className="button-hover icon-button"
                                                    title="Supprimer ce point de vente"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* CSV Upload Tab Content */}
            {activeTab === 'upload' && (
                <div style={worldmapStyles.formSection}>
                    <h3 style={{ marginBottom: '20px', color: '#212529' }}>
                        <span style={{ marginRight: '10px' }}>📊</span>
                        Processeur CSV pour Points de Vente
                    </h3>
                    <POSProcessor />
                </div>
            )}
        </div>
    );
};

export default POSManager;